/**
 * Splits a file name into a main part and an extension suffix so the extension
 * can be pinned (never truncated) while the main part ellipsizes in the middle.
 *
 * Exported because anything that edits a file name has to pin the extension the
 * same way — a second copy of this rule is how the two drift apart.
 */
export const splitFileName = (fileName: string): [string, string] => {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot > 0) {
    return [fileName.slice(0, lastDot), fileName.slice(lastDot)];
  }
  return [fileName, ''];
};

/**
 * Whether a whole name smuggles an extension into a document that has none to
 * pin. With no real extension there is no pinned suffix, so typed text like
 * `.pdf` would read as the document's file type — over what may be non-PDF
 * bytes. Once a real extension exists it stays pinned outside the editable
 * part, and a dot in the name is just a dot.
 */
export const hasDisguisedExtension = (
  wholeName: string,
  originalFileName: string
): boolean => {
  if (splitFileName(originalFileName)[1] !== '') {
    return false;
  }

  const candidate = wholeName.trim();
  // A bare extension ('.pdf') disguises just as well as a trailing one. Only
  // a file that was dotfile-shaped to begin with keeps dotfile-shaped names.
  const isBareExtension =
    /^\.[^.]+$/u.test(candidate) && !originalFileName.startsWith('.');

  return splitFileName(candidate)[1] !== '' || isBareExtension;
};

/**
 * What a name reads as, so names that look alike compare equal. Legacy rows
 * hold what the old upload path stored (see the ui-utils benefit/employer
 * documentsApiHandler encoders): encoded, underscored, NFKD-normalised names.
 * Undoing all three over-detects — the right way to err when the alternative
 * is two cards nobody can tell apart. Shrinks to NFKD + case + trim once the
 * legacy-name migration has run.
 */
const canonicalize = (value: string): string => {
  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    // A stray '%' was never an encoding, so the raw value is what to compare.
  }

  return decoded.normalize('NFKD').replaceAll('_', ' ').trim().toLowerCase();
};

/**
 * Whether another document in the list already reads this way. Whole name to
 * whole name, so `report.pdf` and `report.docx` differ. Keeping your own name
 * is not a collision, excluded canonically so a legacy twin cannot freeze the
 * rename.
 *
 * The one duplicate-name rule: `DocumentCard`'s inline rename and the employer
 * document form both consume it, so the two surfaces cannot drift.
 */
export const isFileNameUsed = (
  fileName: string,
  ownFileName: string,
  existingFileNames?: string[]
): boolean => {
  if (!existingFileNames) {
    return false;
  }

  const candidate = canonicalize(fileName);
  const ownName = canonicalize(ownFileName);

  return existingFileNames.some((name) => {
    const existingName = canonicalize(name);
    return existingName !== ownName && existingName === candidate;
  });
};
